import React, { useState, useCallback, useMemo, memo } from "react";
import TripUsers from "@tripbuilder/trip-header/TripUsers";
import BasicInfo from "@tripbuilder/trip-header/basicInfo";
import EditTripInfo from "@tripbuilder/trip-header/editTripInfo";
import { useToast } from "@/hooks/use-toast";
import useAuthStore from "@/stores/user/authStore";
import { Participant, TripUser } from "@/types";
import TutorialButton from "@tripbuilder/main/Tutorial";
import CopyTrip from "@tripbuilder/main/CopyTrip";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@heroui/tooltip";
import logger from "@/utils/logger";

// API
import { removeTripParticipant } from "@/api/user";
import useTrip from "@/stores/tripBuilder/useTrip";

const ViewOnlyTooltip = memo(() => (
  <Tooltip
    content={
      <p className="text-xs font-font-light">You can only view this trip</p>
    }
    placement="bottom"
    showArrow
    delay={500}
    closeDelay={0}
  >
    <div className="flex items-center justify-center bg-neutral-100 h-8 w-8 rounded-full space-x-2">
      <EyeIcon className="w-4 h-4" />
    </div>
  </Tooltip>
));

interface HeaderProps {
  tripName: string;
  startDate: string;
  endDate: string;
}

const TripHeader: React.FC = memo(() => {
  const tripData = useTrip((state) => state.tripData);
  const setTripData = useTrip((state) => state.setTripData);
  const isParticipant = useTrip((state) => state.isParticipant);
  const copyTrip = useTrip((state) => state.copyTrip);
  const checkIsAdmin = useTrip((state) => state.checkIsAdmin);

  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { user: currentUser } = useAuthStore();
  const { toast } = useToast();
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

  // Memoize static values
  const headerProps = useMemo<HeaderProps>(
    () => ({
      tripName: tripData?.name || "",
      startDate: tripData?.locations?.[0]?.date?.start_date || "",
      endDate:
        tripData?.locations?.[tripData?.locations?.length - 1]?.date
          ?.end_date || "",
    }),
    [tripData?.name, tripData?.locations]
  );

  const handleRemoveUser = useCallback(
    async (user: TripUser) => {
      if (!tripData) return;

      // Optimistically update UI by removing user from trip users and all locations' participants
      const updatedLocations = tripData.locations.map((location) => ({
        ...location,
        participants: location.participants.filter(
          (p: Participant) => p.id !== user.user_id
        ),
      }));

      setTripData({
        ...tripData,
        users: tripData.users.filter(
          (u: TripUser) => u.user_id !== user.user_id
        ),
        locations: updatedLocations,
      });

      try {
        await removeTripParticipant({
          tripId: tripData.id,
          userId: user.user_id,
          removerId: currentUser!.user_id,
        });

        toast({
          title: "User removed from trip",
          description: `${user.first_name} has been removed from the trip.`,
          duration: 5000,
        });
      } catch (error: any) {
        logger.log("error", error);

        // Revert optimistic update on error
        setTripData({
          ...tripData,
          users: [...tripData.users],
        });

        if (error.response.data.error === "Cannot remove the last admin") {
          toast({
            title: "Cannot remove the last admin",
            description: "There must be at least one admin in the trip.",
            duration: 5000,
          });
        }

        toast({
          title: "Error removing user",
          description: "There was an error removing the user from the trip.",
          duration: 5000,
        });
      }
    },
    [tripData, setTripData, currentUser, toast]
  );

  const handleCopyTrip = useCallback(async () => {
    if (!tripData) return;
    setIsLoading(true);
    try {
      await copyTrip(tripData.id);
      toast({
        title: "Trip copied successfully",
        description: "A copy of this trip has been added to your trips.",
      });
      setIsCopied(true);
    } catch (error) {
      console.error("Failed to copy trip:", error);
      toast({
        title: "Error copying trip",
        description: "There was an error copying the trip.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [tripData, copyTrip, toast, setIsLoading]);

  return (
    <div
      className="w-full flex flex-row items-center justify-start space-x-3 mt-2 mb-1.5"
      data-tutorial="basic-info"
    >
      <BasicInfo {...headerProps} />
      <TutorialButton />
      {isParticipant && <TripUsers onRemoveUser={handleRemoveUser} />}
      {isAdmin && <EditTripInfo />}
      {!isParticipant && (
        <>
          <ViewOnlyTooltip />
          <CopyTrip
            onCopy={handleCopyTrip}
            isLoading={isLoading}
            isCopied={isCopied}
            setIsCopied={setIsCopied}
          />
        </>
      )}
    </div>
  );
});

TripHeader.displayName = "TripHeader";

export default TripHeader;
